<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import type { IdeaWithVotes } from '../types';
import ConfirmDialog from './ConfirmDialog.vue';
import HandHelping from './icons/HandHelping.vue';
import AlertCircle from './icons/AlertCircle.vue';
import Heart from './icons/Heart.vue';
import Trash2 from './icons/Trash2.vue';
import X from './icons/X.vue';
import Edit from './icons/Edit.vue';
import MessageSquare from './icons/MessageSquare.vue';

interface Props {
  idea: IdeaWithVotes | null;
  isOpen: boolean;
  canWrite?: boolean;
  currentUserId?: string;
  userRole?: 'owner' | 'member' | 'reader' | null;
  votesLocked?: boolean;
  commentsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  canWrite: true,
  votesLocked: false,
  commentsEnabled: true,
});

// Determine if user can edit/delete this idea
const canEdit = computed(() => {
  if (props.userRole === 'owner') return true;
  if (props.userRole === 'member') {
    return props.idea?.createdBy === props.currentUserId;
  }
  return false;
});

const emit = defineEmits({
  close: () => true,
  vote: (_type: 'liked' | 'important' | 'supported', _onError?: () => void) => true,
  tagClick: (_tag: string) => true,
  delete: () => true,
  edit: () => true,
  comment: () => true,
});

const showDeleteConfirm = ref(false);

function handleDelete() {
  showDeleteConfirm.value = true;
}

function confirmDelete() {
  showDeleteConfirm.value = false;
  emit('delete');
}

function cancelDelete() {
  showDeleteConfirm.value = false;
}

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  },
  { immediate: true }
);

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.isOpen) {
    emit('close');
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

const colorClasses = {
  yellow: 'bg-(--color-postit-yellow)',
  pink: 'bg-(--color-postit-pink)',
  blue: 'bg-(--color-postit-blue)',
  green: 'bg-(--color-postit-green)',
  orange: 'bg-[var(--color-postit-orange)]',
};

// Optimistic vote states (overrides props)
const optimisticVotes = ref<{
  liked: boolean | null;
  important: boolean | null;
  supported: boolean | null;
}>({ liked: null, important: null, supported: null });

// Optimistic vote counts
const optimisticVoteCounts = ref<{
  liked: number | null;
  important: number | null;
  supported: number | null;
}>({ liked: null, important: null, supported: null });

// Track toggle states from Convex userVotes - use ref for reactivity
const voteStates = computed(() => ({
  liked: optimisticVotes.value.liked ?? props.idea?.userVotes?.liked ?? false,
  important: optimisticVotes.value.important ?? props.idea?.userVotes?.important ?? false,
  supported: optimisticVotes.value.supported ?? props.idea?.userVotes?.supported ?? false,
}));

// Computed vote counts that use optimistic values when available
const voteCounts = computed(() => ({
  liked: optimisticVoteCounts.value.liked ?? props.idea?.liked ?? 0,
  important: optimisticVoteCounts.value.important ?? props.idea?.important ?? 0,
  supported: optimisticVoteCounts.value.supported ?? props.idea?.supported ?? 0,
}));

// Watch for changes in the idea prop to reset optimistic state
watch(
  () => props.idea?.id,
  () => {
    optimisticVotes.value = { liked: null, important: null, supported: null };
    optimisticVoteCounts.value = { liked: null, important: null, supported: null };
  }
);

function handleVote(type: 'liked' | 'important' | 'supported') {
  // Check if votes are locked
  if (props.votesLocked) return;

  // Optimistic update: immediately update UI
  const currentHasVote = optimisticVotes.value[type] ?? props.idea?.userVotes?.[type] ?? false;
  const currentCount = optimisticVoteCounts.value[type] ?? props.idea?.[type] ?? 0;

  // Toggle the vote state
  optimisticVotes.value[type] = !currentHasVote;
  // Update the count (add if voting, subtract if unvoting)
  optimisticVoteCounts.value[type] = currentHasVote ? currentCount - 1 : currentCount + 1;

  // Emit to parent with error callback for revert
  emit('vote', type, () => {
    // Revert on error
    optimisticVotes.value[type] = currentHasVote;
    optimisticVoteCounts.value[type] = currentCount;
  });
}

function handleTagClick(tag: string) {
  emit('tagClick', tag);
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-full"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-full"
    >
      <div
        v-if="isOpen && idea"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
        @click="emit('close')"
      >
        <div
          :class="[
            colorClasses[idea.color],
            'w-full sm:w-full sm:max-w-md rounded-lg shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto',
          ]"
          :style="{ transform: `rotate(${idea.rotation}deg)` }"
          @click.stop
        >
          <!-- Paper texture -->
          <div class="paper-texture"></div>

          <!-- Header -->
          <div class="relative p-4 border-b border-black/10">
            <button
              @click="emit('close')"
              class="absolute right-4 top-4 p-1 hover:bg-black/10 rounded-full transition-colors cursor-pointer"
            >
              <X class="w-5 h-5" />
            </button>
            <h2 class="text-2xl font-bold pr-8">{{ idea.title }}</h2>
            <p v-if="idea.person" class="text-sm opacity-70 mt-1">
              {{ $t('idea.by') }} {{ idea.person }}
            </p>
          </div>

          <!-- Content -->
          <div class="relative p-4 space-y-4">
            <!-- Explanation -->
            <p v-if="idea.explanation" class="text-base opacity-90">
              {{ idea.explanation }}
            </p>

            <!-- Tags -->
            <div v-if="idea.tags.length > 0" class="flex flex-wrap gap-2">
              <span
                v-for="tag in idea.tags"
                :key="tag"
                @click="handleTagClick(tag)"
                class="px-3 py-1 bg-black/10 rounded-full text-sm cursor-pointer hover:bg-black/20 transition-colors capitalize"
              >
                {{ tag }}
              </span>
            </div>

            <!-- Voting - Toggle UI -->
            <div class="space-y-3 pt-2">
              <!-- Liked Toggle -->
              <div
                class="flex items-center justify-between p-3 bg-white/50 rounded-lg"
                :class="{ 'opacity-60': votesLocked }"
              >
                <div class="flex items-center gap-2">
                  <Heart class="w-5 h-5 text-[var(--color-dot-liked)]" />
                  <span class="font-medium">{{ $t('votes.liked') }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <button
                    @click="handleVote('liked')"
                    :disabled="votesLocked"
                    :class="[
                      'relative w-12 h-7 rounded-full transition-all duration-200 cursor-pointer',
                      votesLocked
                        ? 'bg-gray-400 cursor-not-allowed'
                        : voteStates.liked
                          ? 'bg-[var(--color-dot-liked)]'
                          : 'bg-gray-300',
                    ]"
                  >
                    <span
                      :class="[
                        'absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200',
                        voteStates.liked ? 'translate-x-5' : 'translate-x-0',
                      ]"
                    />
                  </button>
                  <span class="font-bold text-lg w-8 text-center">{{ voteCounts.liked }}</span>
                </div>
              </div>

              <!-- Important Toggle -->
              <div
                class="flex items-center justify-between p-3 bg-white/50 rounded-lg"
                :class="{ 'opacity-60': votesLocked }"
              >
                <div class="flex items-center gap-2">
                  <AlertCircle class="w-5 h-5 text-[var(--color-dot-important)]" />
                  <span class="font-medium">{{ $t('votes.important') }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <button
                    @click="handleVote('important')"
                    :disabled="votesLocked"
                    :class="[
                      'relative w-12 h-7 rounded-full transition-all duration-200 cursor-pointer',
                      votesLocked
                        ? 'bg-gray-400 cursor-not-allowed'
                        : voteStates.important
                          ? 'bg-[var(--color-dot-important)]'
                          : 'bg-gray-300',
                    ]"
                  >
                    <span
                      :class="[
                        'absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200',
                        voteStates.important ? 'translate-x-5' : 'translate-x-0',
                      ]"
                    />
                  </button>
                  <span class="font-bold text-lg w-8 text-center">{{ voteCounts.important }}</span>
                </div>
              </div>

              <!-- Supported Toggle -->
              <div
                class="flex items-center justify-between p-3 bg-white/50 rounded-lg"
                :class="{ 'opacity-60': votesLocked }"
              >
                <div class="flex items-center gap-2">
                  <HandHelping class="w-5 h-5 text-[var(--color-dot-supported)]" />
                  <span class="font-medium">{{ $t('votes.supported') }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <button
                    @click="handleVote('supported')"
                    :disabled="votesLocked"
                    :class="[
                      'relative w-12 h-7 rounded-full transition-all duration-200 cursor-pointer',
                      votesLocked
                        ? 'bg-gray-400 cursor-not-allowed'
                        : voteStates.supported
                          ? 'bg-[var(--color-dot-supported)]'
                          : 'bg-gray-300',
                    ]"
                  >
                    <span
                      :class="[
                        'absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200',
                        voteStates.supported ? 'translate-x-5' : 'translate-x-0',
                      ]"
                    />
                  </button>
                  <span class="font-bold text-lg w-8 text-center">{{ voteCounts.supported }}</span>
                </div>
              </div>
            </div>

            <!-- Add Comment button -->
            <button
              v-if="commentsEnabled"
              @click="emit('comment')"
              class="w-full px-4 py-3 bg-white/70 hover:bg-white text-gray-800 rounded-lg hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <MessageSquare class="w-4 h-4" />
              {{ $t('comments.add') }}
              <span v-if="idea?.commentCount" class="ml-1 text-sm text-gray-600">
                ({{ idea.commentCount }})
              </span>
            </button>

            <!-- Edit - only show if user can edit -->
            <button
              v-if="canEdit"
              @click="emit('edit')"
              class="w-full px-4 py-3 bg-(--color-primary) text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <Edit class="w-4 h-4" />
              {{ $t('idea.edit') }}
            </button>

            <!-- Delete - only show if user can edit -->
            <button
              v-if="canEdit"
              @click="handleDelete"
              class="w-full px-4 py-3 bg-[var(--color-destructive)] text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <Trash2 class="w-4 h-4" />
              {{ $t('idea.delete') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete confirmation dialog -->
    <ConfirmDialog
      v-model="showDeleteConfirm"
      :title="$t('confirm.deleteIdeaTitle')"
      :message="$t('confirm.deleteIdea')"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      confirm-color="destructive"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </Teleport>
</template>
